import { Box, Typography } from "@mui/material";
import React from "react";
import { Link as RouterLink } from "react-router-dom";

interface MerchBoundaryProps {
  children: React.ReactNode;
}

interface MerchBoundaryState {
  failed: boolean;
}

const INK = "var(--textColor, #f4efe6)";

const GOLD = "var(--logoColor, #c9a84c)";

export class MerchBoundary extends React.Component<MerchBoundaryProps, MerchBoundaryState> {
  state: MerchBoundaryState = { failed: false };

  static getDerivedStateFromError(): MerchBoundaryState {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    if (typeof console !== "undefined") console.error("Merch section failed to render", error);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <Box sx={{ maxWidth: "640px", mx: "auto", textAlign: "center", py: "48px" }}>
        <Typography component="h1" sx={{ fontFamily: "Hanson, sans-serif", fontSize: { xs: "26px", md: "clamp(24px, 1.6rem, 30px)" }, lineHeight: 1.15, color: INK, m: 0 }}>
          The store is having a moment
        </Typography>
        <Typography sx={{ mt: "10px", fontFamily: "Poppins, sans-serif", fontSize: { xs: "16px", md: "15px" }, lineHeight: 1.7, color: INK, opacity: 0.78 }}>
          Something went wrong loading this part of the store. Reloading usually sorts it. Your bag is saved on this device either way.
        </Typography>
        <Box
          component="button"
          type="button"
          onClick={() => window.location.reload()}
          sx={{
            mt: "24px",
            mr: "12px",
            minHeight: 48,
            px: "28px",
            borderRadius: "999px",
            cursor: "pointer",
            border: `1px solid ${GOLD}`,
            background: "transparent",
            color: GOLD,
            fontFamily: "Poppins, sans-serif",
            fontSize: { xs: "15px", md: "14px" },
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            "&:hover": { backgroundColor: GOLD, color: "var(--backgroundColor, #0b0b0c)" },
            "&:focus-visible": { outline: `2px solid ${GOLD}`, outlineOffset: "3px" },
          }}
        >
          Reload
        </Box>
        <Box
          component={RouterLink}
          to="/"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            minHeight: 48,
            px: "12px",
            fontFamily: "Poppins, sans-serif",
            fontSize: { xs: "15px", md: "14px" },
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: INK,
            opacity: 0.75,
            textDecoration: "none",
            "&:hover": { opacity: 1 },
            "&:focus-visible": { outline: `2px solid ${GOLD}`, outlineOffset: "3px", opacity: 1 },
          }}
        >
          Back to the music
        </Box>
      </Box>
    );
  }
}

export default MerchBoundary;
